import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { LandingNav } from "@/components/landing/LandingNav";
import { Container, ContourField, Eyebrow } from "@/components/landing/primitives";
import { DotChip, type DotColor } from "@/components/shared/DotChip";
import { useLanguage } from "@/hooks/useLanguage";
import { publicReleases } from "@/lib/changelog/releases";
import type { ChangeType } from "@/lib/changelog/types";
import { isLandingLocale, makeTr, pick, type LandingLocale } from "@/lib/landing/locale";

/**
 * The public version history ("Novinky"): the public items of every release in
 * src/lib/changelog/releases.ts, newest first. docs/CHANGELOG.md is the
 * internal view of the same data.
 *
 * Prerendered like the landing page: the language comes from the URL, and
 * nothing reads window during render.
 */

const TYPE: Record<ChangeType, { color: DotColor; cs: string; en: string }> = {
  new: { color: "green", cs: "Nové", en: "New" },
  improved: { color: "blue", cs: "Vylepšeno", en: "Improved" },
  fixed: { color: "amber", cs: "Opraveno", en: "Fixed" },
  security: { color: "muted", cs: "Zabezpečení", en: "Security" },
};

const CS_MONTHS = [
  "ledna",
  "února",
  "března",
  "dubna",
  "května",
  "června",
  "července",
  "srpna",
  "září",
  "října",
  "listopadu",
  "prosince",
];
const EN_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const NBSP = "\u00a0";

/** `2026-10-23` as "23. října 2026" / "23 October 2026", the same in Node and every browser. */
function formatReleaseDate(iso: string, locale: LandingLocale): string {
  const [y, m, d] = iso.split("-").map(Number);
  return locale === "cs"
    ? `${d}.${NBSP}${CS_MONTHS[m - 1]} ${y}`
    : `${d}${NBSP}${EN_MONTHS[m - 1]} ${y}`;
}

export default function Changelog() {
  const { lang } = useParams<{ lang: string }>();
  // LanguageRoute redirects an unknown prefix before this renders.
  const locale = isLandingLocale(lang) ? lang : "cs";
  const tr = makeTr(locale);
  const { language, setLanguage } = useLanguage();
  const releases = publicReleases();

  // Keep the app's language in step with the URL.
  useEffect(() => {
    if (locale !== language) setLanguage(locale);
  }, [locale, language, setLanguage]);

  return (
    <div
      lang={locale}
      className="min-h-screen overflow-x-clip bg-background text-foreground antialiased"
    >
      <LandingNav locale={locale} path="/changelog" />

      <main id="obsah">
        <section aria-labelledby="changelog-title" className="relative isolate overflow-hidden">
          <ContourField variant="hero" className="opacity-[0.14] dark:opacity-[0.08]" />
          <Container className="pb-10 pt-14 sm:pb-14 sm:pt-20">
            <Eyebrow className="mb-3">{tr("Novinky", "What's new")}</Eyebrow>
            <h1
              id="changelog-title"
              className="text-[2rem] font-semibold leading-[1.1] tracking-tight sm:text-5xl"
            >
              {tr("Co je v Zettlu nového", "What's new in Zettl")}
            </h1>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              {tr(
                "Nové funkce, vylepšení a opravy, od nejnovějších.",
                "New features, improvements and fixes, newest first."
              )}
            </p>
          </Container>
        </section>

        <Container className="pb-20 sm:pb-28">
          <ol className="max-w-3xl space-y-12 sm:space-y-14">
            {releases.map((r) => (
              <li key={r.version}>
                <article aria-labelledby={`v${r.version}`}>
                  <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b pb-3">
                    <h2 id={`v${r.version}`} className="text-xl font-semibold tracking-tight">
                      <time dateTime={r.date}>{formatReleaseDate(r.date, locale)}</time>
                    </h2>
                    <span className="font-mono text-xs text-muted-foreground">
                      {tr("verze", "version")} {r.version}
                    </span>
                  </header>
                  {r.summary && (
                    <p className="mt-4 text-base leading-relaxed text-foreground/90">
                      {pick(r.summary, locale)}
                    </p>
                  )}
                  <ul className="mt-5 space-y-3">
                    {r.items.map((item) => {
                      const t = TYPE[item.type];
                      return (
                        <li key={item.en} className="grid gap-1 sm:grid-cols-[7.5rem_1fr] sm:gap-4">
                          <DotChip color={t.color} className="self-start sm:pt-[5px]">
                            {tr(t.cs, t.en)}
                          </DotChip>
                          <p className="text-[15px] leading-relaxed">{pick(item, locale)}</p>
                        </li>
                      );
                    })}
                  </ul>
                </article>
              </li>
            ))}
          </ol>
        </Container>
      </main>

      <LandingFooter locale={locale} path="/changelog" />
    </div>
  );
}
